import { IcClose, IcConfirmedBold, IcDataInfoDocument, IcPending } from '@assets/icons/24x24';
import { HStack, Icon, Text } from '@chakra-ui/react';
import { Tooltip } from '@components/ui/tooltip';
import { formatDate } from '@utils/date.utils';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';

import { DateTimeEnum } from '@/types/dateTime.types';
import { ActivationStatusEnum } from '@/types/utm/flightPlan.types';

const getStatusConfig = (status: ActivationStatusEnum, showDescription: boolean) => {
  switch (status) {
    case ActivationStatusEnum.ACCEPTED:
      return {
        icon: IcConfirmedBold,
        color: 'green.500',
        messageId: 'components.flightPlans.validatedOrRejected',
      };
    case ActivationStatusEnum.REJECTED:
      return {
        icon: IcClose,
        color: 'red.500',
        messageId: 'components.flightPlans.validatedOrRejected',
      };
    case ActivationStatusEnum.SUBMITTED:
      return {
        icon: IcPending,
        color: 'orange.500',
        messageId: `${showDescription ? 'components.flightPlans.submitted' : 'flightPlan.status.SUBMITTED'}`,
      };
    case ActivationStatusEnum.DRAFT:
      return {
        icon: IcDataInfoDocument,
        color: 'orange.500',
        messageId: 'flightPlan.status.DRAFT',
      };
  }
};

type Props = {
  status: ActivationStatusEnum;
  showDescription?: boolean;
  validatedOrRejectedBy?: string | null;
  validationOrRejectionTime?: string | null;
};

export default function FlightPlanStatusIcon({
  status,
  showDescription = false,
  validatedOrRejectedBy,
  validationOrRejectionTime,
}: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const config = getStatusConfig(status, showDescription);

  const disabledTooltip =
    [ActivationStatusEnum.ACCEPTED, ActivationStatusEnum.REJECTED].includes(status) &&
    (!validatedOrRejectedBy || !validationOrRejectionTime);
  const date = validationOrRejectionTime ? formatDate(validationOrRejectionTime, DateTimeEnum.DATE) : null;
  const time = validationOrRejectionTime ? format(validationOrRejectionTime, "HH':'mm'") : null;

  const content =
    status === ActivationStatusEnum.ACCEPTED || status === ActivationStatusEnum.REJECTED
      ? formatMessage({ id: config.messageId }, { status, validatedOrRejectedBy, date, time })
      : formatMessage({ id: config.messageId });

  const icon = <Icon as={config.icon} color={config.color} boxSize="24px" data-custom-color />;

  if (showDescription) {
    return (
      <HStack gap={1}>
        {icon}
        <Text fontSize="sm">{content}</Text>
      </HStack>
    );
  }

  return (
    <Tooltip content={content} positioning={{ placement: 'right' }} disabled={disabledTooltip}>
      {icon}
    </Tooltip>
  );
}
